import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBold,
  faItalic,
  faListUl,
  faListOl,
  faParagraph,
} from "@fortawesome/free-solid-svg-icons";

const TaskListIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
    <rect
      x="1"
      y="2"
      width="4"
      height="4"
      rx="0.5"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <rect x="7" y="3.5" width="11" height="1.5" rx="0.75" />
    <rect
      x="1"
      y="8"
      width="4"
      height="4"
      rx="0.5"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <rect x="7" y="9.5" width="11" height="1.5" rx="0.75" />
    <rect
      x="1"
      y="14"
      width="4"
      height="4"
      rx="0.5"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <rect x="7" y="15.5" width="11" height="1.5" rx="0.75" />
  </svg>
);

function ToolbarButton({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      className={`rounded px-2 py-1 text-sm transition-colors ${
        active
          ? "bg-gray-200 text-gray-900"
          : "text-gray-600 hover:bg-gray-200 hover:text-gray-900"
      }`}
    >
      {children}
    </button>
  );
}

function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit, TaskList, TaskItem.configure({ nested: true })],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const incoming = value || "";
    if (editor.getHTML() !== incoming) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="rounded-md border border-gray-300 bg-white overflow-hidden">
      <div className="flex flex-wrap gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold"
        >
          <FontAwesomeIcon icon={faBold} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic"
        >
          <FontAwesomeIcon icon={faItalic} />
        </ToolbarButton>

        <span className="mx-1 h-5 w-px bg-gray-300" />

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          active={editor.isActive("heading", { level: 1 })}
          title="Heading 1"
        >
          <span className="text-sm font-bold leading-none">
            H<sub>1</sub>
          </span>
        </ToolbarButton>
        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          active={editor.isActive("heading", { level: 2 })}
          title="Heading 2"
        >
          <span className="text-sm font-bold leading-none">
            H<sub>2</sub>
          </span>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setParagraph().run()}
          active={false}
          title="Paragraph"
        >
          <FontAwesomeIcon icon={faParagraph} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Bullet List"
        >
          <FontAwesomeIcon icon={faListUl} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Numbered List"
        >
          <FontAwesomeIcon icon={faListOl} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          active={editor.isActive("taskList")}
          title="Task List"
        >
          <TaskListIcon />
        </ToolbarButton>
      </div>
      <EditorContent
        editor={editor}
        className="rich-text-editor min-h-[160px] px-3 py-2.5 text-sm text-gray-900 focus-within:outline-none"
      />
    </div>
  );
}

export default RichTextEditor;
